import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useShallow } from 'zustand/react/shallow';
import { EmptyState } from '@/components/ui';
import { computeStatusCounts, isBackEnabled } from '@/lib/bulk/pairing';
import { cn } from '@/lib/utils';
import {
  selectActiveProducts,
  useBulkCreationStore,
} from '@/stores/bulkCreationStore';
import { MotifTable } from './MotifTable';
import { PairingStatusLine } from './PairingStatusLine';
import { UploadZone } from './UploadZone';
import type { UseMotifUploadsResult } from './useMotifUploads';

export interface MotifsStepProps {
  /**
   * The upload runner. The flow page owns it so the queue and the local
   * previews survive a switch to another step while uploads still run.
   */
  uploads: UseMotifUploadsResult;
  className?: string;
}

/**
 * Step 02 of Bulk Creation (plan U9, R14–R26, R58): front and back upload
 * zones, the pairing status line, the row table and the product total.
 * Uploads start right after a drop and finish as assets; the cell states
 * live in the bulk store, which also decides whether the step is complete.
 * Header, pills and CTA belong to the flow frame (U10).
 */
export function MotifsStep({ uploads, className }: MotifsStepProps) {
  const { t } = useTranslation();
  const { reference, activeProductIds, rows } = useBulkCreationStore(
    useShallow((state) => ({
      reference: state.reference,
      activeProductIds: state.activeProductIds,
      rows: state.rows,
    }))
  );
  const { localFiles, startUploads } = uploads;

  const activeProducts = useMemo(
    () => selectActiveProducts({ reference, activeProductIds }),
    [reference, activeProductIds]
  );
  const backEnabled = isBackEnabled(activeProducts);
  const counts = useMemo(
    () => computeStatusCounts(rows, activeProducts),
    [rows, activeProducts]
  );

  return (
    <section
      aria-labelledby="bulk-motifs-title"
      className={cn('flex flex-col gap-6', className)}
    >
      <div className="flex flex-col gap-1.5">
        <h2 id="bulk-motifs-title" className="dc-h2 text-xl text-ink">
          {t('seller.bulk.motifs.title')}
        </h2>
        <p className="max-w-prose text-sm text-ink-2">
          {t('seller.bulk.motifs.intro')}
        </p>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <UploadZone zone="front" onUploadsAdded={startUploads} />
        <UploadZone
          zone="back"
          disabled={!backEnabled}
          onUploadsAdded={startUploads}
        />
      </div>

      {rows.length === 0 ? (
        <EmptyState
          title={t('seller.bulk.motifs.empty.title')}
          description={t('seller.bulk.motifs.empty.text')}
        />
      ) : (
        <div className="flex flex-col gap-3">
          <PairingStatusLine counts={counts} />
          <MotifTable
            rows={rows}
            activeProducts={activeProducts}
            backEnabled={backEnabled}
            placements={reference?.referencePlacement}
            localFiles={localFiles}
          />
          <p
            aria-live="polite"
            data-testid="bulk-motifs-footer"
            className="self-end font-mono text-[13px] font-medium text-ink"
          >
            {t('seller.bulk.motifs.footerTotal', {
              count: counts.totalProducts,
            })}
          </p>
        </div>
      )}
    </section>
  );
}
